import { useCallback, useEffect, useState } from 'react';
import {
  EDITOR_NODE_DRAG_TYPE,
  EDITOR_ROOT_NODE_DRAG_TYPE,
  getNestedDropPosition,
  hasDragType,
  nestedDropOperation,
  readEditorDragNodeId,
  writeEditorDragData,
} from './utilities/dragAndDrop.js';

/**
 * Owns the browser drag lifecycle for recursive EditItems. Root items use the
 * separate list drag container, so their marker is ignored at this boundary.
 */
export const useNestedElementDrag = ({
  nodeId,
  depth,
  enabled,
  canMove,
  canAddChild,
  canContainElements,
  onMoveNode,
}) => {
  const [dropPosition, setDropPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const canDrag = depth > 0 && enabled && canMove;
  const canAcceptDrop = enabled && canMove;
  const canAcceptChildDrop = enabled && canAddChild && canContainElements;

  const clearDragState = useCallback(() => {
    setIsDragging(false);
    setDropPosition(null);
  }, []);

  useEffect(() => {
    if (!enabled) clearDragState();
  }, [clearDragState, enabled]);

  const calculateDropPosition = useCallback((event) => {
    const targetRect = event.currentTarget.getBoundingClientRect();
    return getNestedDropPosition({
      pointerX: event.clientX - targetRect.left,
      pointerY: event.clientY - targetRect.top,
      width: targetRect.width,
      height: targetRect.height,
      canHaveChildren: canContainElements && canAddChild,
    });
  }, [canAddChild, canContainElements]);

  const supportsDrop = useCallback((event, allowRoot = false) => {
    const dataTransfer = event.dataTransfer;
    if (!hasDragType(dataTransfer, EDITOR_NODE_DRAG_TYPE)) return false;

    const isRootListDrag = hasDragType(dataTransfer, EDITOR_ROOT_NODE_DRAG_TYPE);
    return allowRoot || depth > 0 || !isRootListDrag;
  }, [depth]);

  const acceptDropEvent = useCallback((event, position) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    setDropPosition(position);
  }, []);

  const moveDraggedNode = useCallback((event, position) => {
    const sourceNodeId = readEditorDragNodeId(event.dataTransfer);
    clearDragState();

    if (sourceNodeId && sourceNodeId !== nodeId) {
      onMoveNode?.(sourceNodeId, nodeId, nestedDropOperation(position));
    }
  }, [clearDragState, nodeId, onMoveNode]);

  const handleDragStart = useCallback((event) => {
    if (!canDrag) return;

    event.stopPropagation();
    if (writeEditorDragData(event.dataTransfer, { nodeId })) {
      setIsDragging(true);
    } else {
      event.preventDefault();
    }
  }, [canDrag, nodeId]);

  const handleDragOver = useCallback((event) => {
    if (!canAcceptDrop || !supportsDrop(event)) return;

    acceptDropEvent(event, calculateDropPosition(event));
  }, [acceptDropEvent, calculateDropPosition, canAcceptDrop, supportsDrop]);

  const handleDrop = useCallback((event) => {
    if (!canAcceptDrop || !supportsDrop(event)) return;

    event.preventDefault();
    event.stopPropagation();
    const nextPosition = calculateDropPosition(event);
    moveDraggedNode(event, nextPosition);
  }, [calculateDropPosition, canAcceptDrop, moveDraggedNode, supportsDrop]);

  // An explicit child target avoids ambiguous before/after geometry in empty containers.
  // Root-list drags are accepted here because stopping propagation prevents list reordering.
  const handleChildDragOver = useCallback((event) => {
    if (!canAcceptChildDrop || !supportsDrop(event, true)) return;

    acceptDropEvent(event, 'child');
  }, [acceptDropEvent, canAcceptChildDrop, supportsDrop]);

  const handleChildDrop = useCallback((event) => {
    if (!canAcceptChildDrop || !supportsDrop(event, true)) return;

    event.preventDefault();
    event.stopPropagation();
    moveDraggedNode(event, 'child');
  }, [canAcceptChildDrop, moveDraggedNode, supportsDrop]);

  const handleDragLeave = useCallback((event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDropPosition(null);
    }
  }, []);

  return {
    active: enabled,
    canDrag,
    clearDragState,
    dropPosition,
    handleChildDragOver,
    handleChildDrop,
    handleDragLeave,
    handleDragOver,
    handleDragStart,
    handleDrop,
    isDragging,
  };
};

export default useNestedElementDrag;
